import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { WebexCallingFieldConfigResource } from '../../shared/resources/field-config/webex-calling-field-config.resource';
import {
  CiscoHelpdeskOptions,
  Global360View,
  WebexDialPlanFieldConfig,
  WebexEndUser,
  WebexFieldConfig,
  WebexFieldConfigV2,
} from '../../../shared/models/generated/ziroModels';
import { cloneDeep, get, isArray, isEqual } from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { WebexUserResource } from '../../../shared/resources/webex-user.resource';
import { combineLatest, Observable, of, Subscription, switchMap } from 'rxjs';
import { DidSelectorFormData, ZiroDidSelectorFormComponent } from './did-selector-form/did-selector-form.component';
import {
  ExtensionDetailsEntity,
  ZiroExtensionDetailsFormComponent,
} from './extension-details-form/extension-details-form.component';
import { ZiroFormsUpdate } from '../../../forms/ziro-forms-models';
import { ButtonStyles } from '../../../button/button.component';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ToastService } from '../../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { UserDetailUiContext } from '../../shared/contexts/user-detail-ui.context';
import { Global360ViewContext } from '../../../shared/contexts/global-360-view.context';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { map, shareReplay, tap } from 'rxjs/operators';
import { HelpdeskOptionsContext } from '../../shared/contexts/helpdesk-options.context';

export const UCM_LICENSE_NAME = 'Unified Communication Manager (UCM)';

@Component({
  selector: 'ziro-webex-calling',
  templateUrl: './webex-calling.component.html',
  standalone: false,
})
export class WebexCallingComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _ziroModalService = inject(ZiroModalService);
  private _webexUserResource = inject(WebexUserResource);
  private _userDetailUiContext = inject(UserDetailUiContext);
  private _global360ViewContext = inject(Global360ViewContext);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _fieldConfigResource = inject(WebexCallingFieldConfigResource);
  private _ziroFormStateService = inject(ZiroFormStateService);
  private _helpdeskOptionsContext = inject(HelpdeskOptionsContext);

  @ViewChild(ZiroDidSelectorFormComponent) didSelectorFormComponent: ZiroDidSelectorFormComponent;
  @ViewChild(ZiroExtensionDetailsFormComponent) extensionDetailsFormComponent: ZiroExtensionDetailsFormComponent;

  webexFieldConfigV1: WebexFieldConfig;
  webexFieldConfigV2: WebexFieldConfigV2;
  didSelectorFormData: DidSelectorFormData;
  extensionDetailsEntity: ExtensionDetailsEntity;
  dialPlanFieldConfig: WebexDialPlanFieldConfig[];
  isLoading = true;
  enableWebexPerSite: boolean;
  parentSubmit$: Observable<void>;

  private _email: string;
  private _initialWebexEndUser: WebexEndUser;
  private _isExistingUser = true;
  private _ziroIcons = ZiroIcons;
  private _username: string;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._email = get(this._route, 'snapshot.params.email');
    this._username = get(this._route, 'snapshot.params.username');

    const initSub = combineLatest([
      this._webexUserResource.get(this._email),
      this._helpdeskOptionsContext.state$,
      this._userDetailUiContext.state$,
    ])
      .pipe(
        switchMap(([webexEndUser, helpDeskOptions]: [WebexEndUser, CiscoHelpdeskOptions, number]) => {
          this._initialWebexEndUser = webexEndUser;
          this._isExistingUser = !!webexEndUser.webexCallingLicense;
          this.enableWebexPerSite = helpDeskOptions.enableWebexPerSite;

          if (!this.enableWebexPerSite) {
            return this._fieldConfigResource.getFieldConfigs(this._email);
          }

          return combineLatest([
            this._fieldConfigResource.getFieldConfigs(this._email),
            this._fieldConfigResource.getFieldConfigsV2(this._userDetailUiContext.getCurrentSite().id),
          ]);
        }),
        switchMap((config) => {
          if (isArray(config)) {
            this.webexFieldConfigV1 = config[0];
            this.webexFieldConfigV2 = config[1];
          } else {
            this.webexFieldConfigV1 = config;
          }

          return this._fieldConfigResource.getDialPlanFieldConfigs();
        }),
      )
      .subscribe((webexDialPlanFieldConfig) => {
        this.dialPlanFieldConfig = webexDialPlanFieldConfig;

        const licenseFieldConfig = this.enableWebexPerSite
          ? this.webexFieldConfigV2.licenseType
          : this.webexFieldConfigV1.webexCallingLicenseJson;

        this.didSelectorFormData = {
          webexCallingLicense: licenseFieldConfig?.defaultValue || '',
          location: this.enableWebexPerSite
            ? this.webexFieldConfigV2?.location?.locationName
            : this._initialWebexEndUser.location,
          did: this._initialWebexEndUser.did,
        };

        this.extensionDetailsEntity = {
          extension: this._isExistingUser
            ? this._initialWebexEndUser.extension
            : this.webexFieldConfigV1.extension.defaultValue,
        };

        this._initBottomNav();
        this.isLoading = false;
      });

    this._subscriptions.add(initSub);
  }

  ngOnDestroy(): void {
    this._bottomNavService.dispatch(new BottomNavClearButtonsList());
    this._subscriptions.unsubscribe();
  }

  onFormUpdate($event: ZiroFormsUpdate<DidSelectorFormData | ExtensionDetailsEntity>, form: string): void {
    if ($event && !isEqual($event.new, $event.old)) {
      switch (form) {
        case 'DID_SELECTOR_FORM':
          this.didSelectorFormData = { ...$event.new } as DidSelectorFormData;
          break;
        case 'EXTENSION_DETAILS_FORM':
          this.extensionDetailsEntity = { ...$event.new } as ExtensionDetailsEntity;
          break;
      }
    }
  }

  private _initBottomNav(): void {
    const bottomNavButtons = [];

    bottomNavButtons.push({
      id: 'webex-form-cancel-button',
      dataAutomation: 'webex-form-cancel-button',
      label: 'tkey;dialogs.button.cancel',
      buttonClass: ButtonStyles.DEFAULT,
      cb: () => this._goToUserHome(),
    });

    if (this._isExistingUser) {
      bottomNavButtons.push({
        id: 'webex-form-delete-button',
        dataAutomation: 'webex-form-delete-button',
        label: 'tkey;dialogs.button.delete',
        icon: this._ziroIcons.DELETE_SMALL,
        buttonClass: ButtonStyles.DANGER,
        cb: () => this._onDelete(),
      });
    }

    bottomNavButtons.push({
      id: 'webex-form-save-button',
      dataAutomation: 'webex-form-save-button',
      label: 'tkey;global.button.save.text',
      icon: this._ziroIcons.SAVE_SUCCESS,
      buttonClass: ButtonStyles.PRIMARY,
      cb: () => this._onSave(),
    });

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _goToUserHome(): void {
    this._bottomNavService.dispatch(new BottomNavClearButtonsList());
    this._router.navigateByUrl(`/user/${encodeURIComponent(this._route.snapshot.params.username)}`);
  }

  private _areFormsInvalid(): boolean {
    return !this.didSelectorFormComponent.isFormValid() || !this.extensionDetailsFormComponent?.isFormValid();
  }

  private _onSave(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
      }),
    );
    if (this._areFormsInvalid()) {
      this.parentSubmit$ = of(null);
      this._setFormSubmitState(true);
      this._bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: true,
        }),
      );
    } else {
      this._setPendingState('save');

      const entityCopy: WebexEndUser =
        this.didSelectorFormData?.webexCallingLicense === UCM_LICENSE_NAME
          ? {
              ...this._initialWebexEndUser,
              webexCallingLicense: this.didSelectorFormData.webexCallingLicense,
              location: '',
              did: '',
              extension: '',
            }
          : {
              ...this._initialWebexEndUser,
              webexCallingLicense: this.didSelectorFormData.webexCallingLicense,
              did: this.didSelectorFormData.did,
              location: this.didSelectorFormData.location,
              extension: this.extensionDetailsEntity.extension,
            };

      this.parentSubmit$ = (
        this._isExistingUser
          ? this._webexUserResource.put(entityCopy, this._email)
          : this._webexUserResource.post(entityCopy)
      ).pipe(
        switchMap(() => {
          this._global360ViewContext._isFetchingSource.next(true);
          return this._global360ViewContext.getGlobal360View(this._username);
        }),
        map((global360: Global360View): null => {
          this._global360ViewContext._stateSource.next(global360);
          this._global360ViewContext._isFetchingSource.next(false);
          this._toastService.push(
            ToastTypes.SUCCESS,
            this._ziroIcons.WEBEX_CALLING_SMALL,
            'tkey;shared.toast.save.success.title',
            `${this._translateService.instant('tkey;shared.service.webex_calling.text')} - ${
              this._telephoneNumberFilter.transform(entityCopy.did || entityCopy.extension) ||
              entityCopy.webexCallingLicense
            }`,
          );
          this._goToUserHome();
          return null;
        }),
        shareReplay(),
      );
      this._setFormSubmitState(true);
    }
  }

  private _onDelete(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: 'text-danger',
        title: this._translateService.instant('tkey;helpdesk.webex_calling.delete.confirmation.modal.header'),
        promptBody: this._translateService.instant('tkey;helpdesk.webex_calling.delete.confirmation.modal.text', {
          displayName: this._initialWebexEndUser.displayName,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._onConfirmDelete(),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _onConfirmDelete(): Observable<void> {
    return this._webexUserResource.delete(this._email).pipe(
      tap(() => {
        this._toastService.pushDeleteToast(
          'tkey;shared.service.webex_calling.text',
          `${this._translateService.instant('tkey;shared.service.webex_calling.text')} - ${
            this._telephoneNumberFilter.transform(
              this._initialWebexEndUser.did || this._initialWebexEndUser.extension,
            ) || this._initialWebexEndUser.webexCallingLicense
          }`,
        );
        const global360 = cloneDeep(this._userDetailUiContext.getGlobal360View());
        global360.webexEndUser = {
          ...global360.webexEndUser,
          webexCallingLicense: null,
          location: null,
          did: null,
          extension: null,
        };
        this._global360ViewContext._stateSource.next(global360);
        this._setFormSubmitState(false);
        this._ziroFormStateService.setIsFormDirty(false);
        this._goToUserHome();
      }),
    );
  }

  private _setFormSubmitState(state: boolean): void {
    if (this.didSelectorFormComponent) {
      this.didSelectorFormComponent._validateAndSubmitSource.next(state);
    }
    if (this.extensionDetailsFormComponent) {
      this.extensionDetailsFormComponent._validateAndSubmitSource.next(state);
    }
  }

  private _setPendingState(type: 'save' | 'delete' | null): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'webex-form-save-button',
        state: {
          pending: type === 'save',
          buttonDisableState: { disabled: type !== null, tooltipKey: '' },
        },
      }),
    );
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'webex-form-cancel-button',
        state: {
          buttonDisableState: { disabled: type !== null, tooltipKey: '' },
        },
      }),
    );
    if (this._isExistingUser) {
      this._bottomNavService.dispatch(
        new BottomNavUpdateButtonState({
          id: 'webex-form-delete-button',
          state: {
            pending: type === 'delete',
            buttonDisableState: { disabled: type !== null, tooltipKey: '' },
          },
        }),
      );
    }
  }
}
